import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { usePathWatcher } from '@hooks/use-path-watcher';

const AppLayoutContext = createContext(null);

const defaultAppLayoutState = {
  closeSideNav: false,
  activeAgent: null,
  activeMenuId: 'dashboard',
  activeSubMenuId: null,
  dynamicMenuItems: [],
  contentAreaPadding: true,
  hideOnboardingWidget: false,
  menuItems: [],
  adminMenuItems: [],
  blockedMenuPaths: [],
  dataTablesMenuDisabled: false,

  // Remove this when UI is done
  showOldTopNav: true,
};

export const AppLayoutProvider = ({ children }) => {
  const [appLayoutState, setAppLayoutState] = useState(() => {
    const storedState = localStorage.getItem('appLayoutState');
    return storedState ? JSON.parse(storedState) : defaultAppLayoutState;
  });

  usePathWatcher(setAppLayoutState, appLayoutState.dynamicMenuItems);

  const updateState = useCallback((partial) => {
    setAppLayoutState((prev) => {
      const hasChanges = Object.keys(partial).some(
        (key) => !Object.is(prev[key], partial[key])
      );
      if (!hasChanges) return prev;
      const updated = { ...prev, ...partial };
      localStorage.setItem('appLayoutState', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const onToggleCloseSideNav = useCallback(
    () =>
      setAppLayoutState((prev) => {
        const updated = { ...prev, closeSideNav: !prev.closeSideNav };
        localStorage.setItem('appLayoutState', JSON.stringify(updated));
        return updated;
      }),
    []
  );

  const onSetSideNavCloseState = useCallback(
    (newState) => updateState({ closeSideNav: newState }),
    [updateState]
  );

  const onSetActiveAgent = useCallback(
    (agent) => updateState({ activeAgent: agent }),
    [updateState]
  );

  const onSetActiveMenu = useCallback(
    (menuId) => updateState({ activeMenuId: menuId }),
    [updateState]
  );

  const onSetActiveSubMenu = useCallback(
    (subMenuId) => updateState({ activeSubMenuId: subMenuId }),
    [updateState]
  );

  const onSetDynamicMenuItems = useCallback(
    (dynamicMenuItems) => updateState({ dynamicMenuItems }),
    [updateState]
  );

  const onSetContentAreaPadding = useCallback(
    (isPadding) => updateState({ contentAreaPadding: isPadding }),
    [updateState]
  );

  const onHideOnboardingWidget = useCallback(
    (state) => updateState({ hideOnboardingWidget: state }),
    [updateState]
  );

  const onSetMenuItems = useCallback(
    (menuItems) => updateState({ menuItems }),
    [updateState]
  );

  const onSetAdminMenuItems = useCallback(
    (adminMenuItems) => updateState({ adminMenuItems }),
    [updateState]
  );

  const onSetMenuAccessState = useCallback(
    ({ blockedMenuPaths, dataTablesMenuDisabled }) =>
      updateState({ blockedMenuPaths, dataTablesMenuDisabled }),
    [updateState]
  );

  const onSetToDefaultState = useCallback(() => {
    setAppLayoutState(defaultAppLayoutState);
    localStorage.setItem(
      'appLayoutState',
      JSON.stringify(defaultAppLayoutState)
    );
  }, []);

  // Remove this when UI is done
  const onSetShowOldTopNav = useCallback(
    (state) => updateState({ showOldTopNav: state }),
    [updateState]
  );

  const contextValue = useMemo(
    () => ({
      appLayoutState,
      onToggleCloseSideNav,
      onSetSideNavCloseState,
      onSetActiveAgent,
      onSetActiveMenu,
      onSetActiveSubMenu,
      onSetDynamicMenuItems,
      onSetContentAreaPadding,
      onHideOnboardingWidget,
      onSetMenuItems,
      onSetAdminMenuItems,
      onSetMenuAccessState,
      onSetToDefaultState,
      // Remove this when UI is done
      onSetShowOldTopNav,
    }),
    [
      appLayoutState,
      onToggleCloseSideNav,
      onSetSideNavCloseState,
      onSetActiveAgent,
      onSetActiveMenu,
      onSetActiveSubMenu,
      onSetDynamicMenuItems,
      onSetContentAreaPadding,
      onHideOnboardingWidget,
      onSetMenuItems,
      onSetAdminMenuItems,
      onSetMenuAccessState,
      onSetToDefaultState,
      onSetShowOldTopNav,
    ]
  );

  return (
    <AppLayoutContext.Provider value={contextValue}>
      {children}
    </AppLayoutContext.Provider>
  );
};

export const useAppLayout = () => {
  const context = useContext(AppLayoutContext);
  if (!context) {
    throw new Error('useAppLayout must be used within an AppLayoutProvider');
  }
  return context;
};
